import { Contact, Link, Member, Statement, Tag, Work } from "@/api/models";
import { notEmptyString } from "@/api/validators";
import { DisplaySetting } from "./schemas";

export interface WindowState {
  height: number;
  width: number;
}

export interface KeyStringValue {
  key: string;
  value: string;
}

export interface KeyAnyValue {
  key: string;
  value: any;
}

export interface CourseItem {
  code: string;
  name: string;
  parent?: string;
}

export interface OptionSet {
  key: string;
  title: string;
  options: Array<KeyStringValue>;
}

export interface OptionGroup {
  name: string;
  fields: Array<OptionSet>;
}

export interface Snippet {
  key: string;
  format: string;
  value: string;
}

export interface MenuItem {
  to: string;
  title: string;
  active: boolean;
  children: Array<MenuItem>;
}

export interface UserState {
  _id: string;
  identifier: string;
  fullName: string;
  role: string;
  mode: string;
  active: boolean;
  lastLogin: Date;
  created: Date;
  modified: Date;
}

export interface ThemeItem {
  text: string;
  slug: string;
  children?: Array<ThemeItem>;
}

export interface KeyName {
  key: string;
  name: string;
}

export interface KeyNameGroups {
  key?: string;
  name: string;
  groups: string[];
}

export interface SiteState {
  labels: Map<string, string>;
  settings: Map<string, any>;
  snippets: Array<Snippet>;
  schools: Array<KeyNameGroups>;
  programmes: Array<KeyName>;
  courses: Array<CourseItem>;
  themes: Array<ThemeItem>;
  definedThemeSlugs: Array<string>;
  mode: string;
  displayOptions: Map<string, DisplaySetting[]>;
  setting: any;
  label: any;
  timedOut: boolean;
  project: SimpleProject;
}

export interface RootState {
  version: number;
}

export interface SubmissionState {
  _id: string;
  firstName: string;
  lastName: string;
  displayName: string;
  description: string;
  slug: string;
  mode: string;
  contacts: Array<Contact>;
  members: Array<Member>;
  hasContacts?: boolean;
  hasLinks?: boolean;
  hasSponsors?: boolean;
  school: string;
  programme: string;
  course: string;
  programmeCode: string;
  courseCode: string;
  notes: string;
  statements: Array<Statement>;
  displayOptions: any;
  links: Array<Link>;
  themes: Array<Tag>;
  subthemes: Array<Tag>;
  tags: Array<Tag>;
  sponsors: Array<Link>;
  works: Array<Work>;
  showQrCode: boolean;
  deadlineException: boolean;
  ineligible: boolean;
  submitted: boolean;
  createdAt: Date;
  modifiedAt: Date;
  modifiedTs: number;
  selectedThemeSlugs: Array<string>;
}

export interface SubNavItem {
  name: string;
  key: string;
  active: boolean;
  icon?: string;
  iconLeft?: string;
  title?: string;
  type?: string;
  classNames?: Array<string>;
  workIndex?: number;
}

export interface OptionItem {
  value: string;
  name: string;
  key?: string;
  active?: boolean;
  type?: string;
  icon?: string;
}

export interface UserNavItem {
  key: string;
  name: string;
  inNav: boolean;
}

export interface BasicMember {
  displayName: string;
  identifier: string;
  description: string;
  slug: string;
  submission?: string;
  isLead?: boolean;
}

export interface SimpleProject {
  _id: string;
  exists: boolean;
  slug: string;
  groupName: string;
  description: string;
  members: BasicMember[];
  imageUri: string;
  themeTree?: ThemeItem;
}

export const defaultSimpleProject: SimpleProject = {
  _id: "",
  exists: false,
  slug: "",
  groupName: "",
  description: "",
  members: [],
  imageUri: "",
  themeTree: {
    slug: "",
    text: "",
    children: [],
  },
};

export const mapThemeTree = (data: any = null): ThemeItem => {
  const tree = {
    text: "",
    slug: "",
    children: [],
  };
  if (data instanceof Object) {
    const { slug, text, subs } = data;
    if (notEmptyString(slug) && notEmptyString(text)) {
      tree.slug = slug;
      tree.text = text;
    }
    if (subs instanceof Array && subs.length > 0) {
      tree.children = subs.map(mapThemeTree);
    }
  }
  return tree;
};

export interface SimpleProfile {
  displayName: string;
  slug: string;
  profileImageUrl: string;
  statement: Statement[];
}
